import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PaginatedTokenUsageRecords, TokenUsageRecord } from '@claude-code-api/shared';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface UsageHistoryResponse {
  data?: PaginatedTokenUsageRecords;
}

export default function UsageHistoryPage() {
  const { t } = useTranslation('usage');
  const { isAdmin } = useAuth();
  const [records, setRecords] = useState<TokenUsageRecord[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    const fetchHistory = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/api/usage/me/history?page=${page}&limit=20`, {
          credentials: 'include',
          signal: controller.signal,
        });
        if (!response.ok) return;

        const result = await response.json() as UsageHistoryResponse;
        if (!result.data) return;
        setRecords(result.data.records);
        setTotalPages(result.data.pagination.totalPages);
      } catch (error) {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          console.error('Failed to fetch usage history:', error);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    void fetchHistory();
    return () => controller.abort();
  }, [page]);

  return (
    <div className="space-y-6">
      <PageHeader title={t('historyTitle')} description={t('historyDescription')} />

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((row) => <Skeleton key={row} className="h-14 rounded-lg" />)}
        </div>
      ) : records.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('noRecords')}</p>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('columns.date')}</TableHead>
                    {isAdmin ? (
                      <>
                        <TableHead>{t('columns.model')}</TableHead>
                        <TableHead className="text-right">{t('columns.inputTokens')}</TableHead>
                        <TableHead className="text-right">{t('columns.outputTokens')}</TableHead>
                      </>
                    ) : null}
                    <TableHead className="text-right">{t('columns.credits')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell className="text-sm">{new Date(record.createdAt).toLocaleString()}</TableCell>
                      {isAdmin ? (
                        <>
                          <TableCell className="text-sm">{record.model}</TableCell>
                          <TableCell className="text-right text-sm">{record.inputTokens.toLocaleString()}</TableCell>
                          <TableCell className="text-right text-sm">{record.outputTokens.toLocaleString()}</TableCell>
                        </>
                      ) : null}
                      <TableCell className="text-right text-sm font-medium">{record.creditsDeducted.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {totalPages > 1 ? (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
                disabled={page <= 1}
                aria-label={t('previousPage')}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground">{t('pagination', { page, totalPages })}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
                disabled={page >= totalPages}
                aria-label={t('nextPage')}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
